import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiFetch } from '../config/api';
import { useAuth } from './AuthContext';
import type {
  BillingPeriod,
  PaymentCurrency,
  PaymentIntent,
  PaymentRecord,
  PlanId,
  PlansResponse,
  QuotaStatus,
  Subscription,
  UsageResponse,
  UsageWindow,
} from '../hooks/useSubscription';

// ─── Context ──────────────────────────────────────────────────────────────

export interface SubscriptionContextValue {
  // State
  plans: PlansResponse | null;
  subscription: Subscription | null;
  usage: UsageResponse | null;
  quotaStatus: QuotaStatus | null;
  paymentHistory: PaymentRecord[];
  isLoadingPlans: boolean;
  isLoadingSubscription: boolean;
  isLoadingUsage: boolean;
  isLoadingPaymentHistory: boolean;
  error: string | null;

  // Actions
  fetchPlans: () => Promise<PlansResponse | null>;
  fetchSubscription: () => Promise<unknown>;
  fetchUsage: () => Promise<UsageResponse | null>;
  fetchPaymentHistory: () => Promise<PaymentRecord[] | null>;
  createPaymentIntent: (
    plan: Exclude<PlanId, 'free'>,
    billingPeriod: BillingPeriod,
    currency?: PaymentCurrency,
  ) => Promise<PaymentIntent | null>;
  verifyPayment: (
    paymentId: string,
    txHash: string,
  ) => Promise<{ ok: true; data: unknown } | { ok: false; message: string }>;
  attachTxHash: (paymentId: string, txHash: string) => Promise<boolean>;
  startPaymentPolling: (
    paymentId: string,
    onConfirmed: () => void,
    timeoutMs?: number,
    txHash?: string,
  ) => void;
  stopPaymentPolling: () => void;
  refreshAll: () => Promise<void>;
  cancelSubscription: () => Promise<boolean>;
  getPrice: (
    plans: PlansResponse,
    planId: Exclude<PlanId, 'free'>,
    billingPeriod: BillingPeriod,
  ) => PlansResponse['prices'][number] | null;

  // Derived
  getUsagePercentage: (window: UsageWindow) => number;
  isQuotaExceeded: (quotaStatus: QuotaStatus | null) => boolean;
  isApproachingLimit: (usage: UsageResponse | null, threshold?: number) => boolean;
  isPendingPayment: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [isLoadingPaymentHistory, setIsLoadingPaymentHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activePaymentIdRef = useRef<string | null>(null);

  const stopPaymentPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  // Clear state when wallet disconnects
  useEffect(() => {
    if (!isAuthenticated) {
      setPlans(null);
      setSubscription(null);
      setUsage(null);
      setQuotaStatus(null);
      setPaymentHistory([]);
      setError(null);
      stopPaymentPolling();
      activePaymentIdRef.current = null;
    }
  }, [isAuthenticated, stopPaymentPolling]);

  // ─── Fetch Plans ─────────────────────────────────────────────────────────

  const fetchPlans = useCallback(async () => {
    setIsLoadingPlans(true);
    setError(null);
    try {
      const response = await apiFetch('/subscription/plans');
      const data = await response.json();
      if (response.ok && data.success) {
        setPlans(data.data);
        return data.data as PlansResponse;
      }
      setError(data.message || 'Failed to fetch plans');
      return null;
    } catch {
      setError('Network error while fetching plans');
      return null;
    } finally {
      setIsLoadingPlans(false);
    }
  }, []);

  // ─── Auto-fetch plans on mount ───────────────────────────────────────────

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  // ─── Fetch Subscription Status ───────────────────────────────────────────

  const fetchSubscription = useCallback(async () => {
    setIsLoadingSubscription(true);
    setError(null);
    try {
      const response = await apiFetch('/subscription/me');
      const data = await response.json();
      if (response.ok && data.success) {
        const d = data.data;
        setSubscription(d.subscription);
        setQuotaStatus(d.quotaStatus);
        setUsage({
          plan: d.effectivePlan,
          usage: d.usage,
        });
        return d;
      }
      setError(data.message || 'Failed to fetch pre-paid subscription');
      return null;
    } catch {
      setError('Network error while fetching pre-paid subscription');
      return null;
    } finally {
      setIsLoadingSubscription(false);
    }
  }, []);

  // ─── Fetch Usage ─────────────────────────────────────────────────────────

  const fetchUsage = useCallback(async () => {
    setIsLoadingUsage(true);
    setError(null);
    try {
      const response = await apiFetch('/subscription/usage');
      const data = await response.json();
      if (response.ok && data.success) {
        setUsage(data.data);
        return data.data as UsageResponse;
      }
      setError(data.message || 'Failed to fetch usage');
      return null;
    } catch {
      setError('Network error while fetching usage');
      return null;
    } finally {
      setIsLoadingUsage(false);
    }
  }, []);

  // ─── Fetch Payment History ───────────────────────────────────────────────

  const fetchPaymentHistory = useCallback(async () => {
    setIsLoadingPaymentHistory(true);
    setError(null);
    try {
      const response = await apiFetch('/subscription/payments');
      const data = await response.json();
      if (response.ok && data.success) {
        setPaymentHistory(data.data);
        return data.data as PaymentRecord[];
      }
      setError(data.message || 'Failed to fetch payment history');
      return null;
    } catch {
      setError('Network error while fetching payment history');
      return null;
    } finally {
      setIsLoadingPaymentHistory(false);
    }
  }, []);

  // ─── Auto-fetch subscription data when authenticated ─────────────────────

  const dataLoaded = subscription !== null;

  const fetchInParallel = useCallback(async () => {
    await Promise.all([fetchSubscription(), fetchUsage(), fetchPaymentHistory()]);
  }, [fetchSubscription, fetchUsage, fetchPaymentHistory]);

  useEffect(() => {
    if (isAuthenticated && !dataLoaded) {
      void fetchInParallel();
    }
  }, [isAuthenticated, dataLoaded, fetchInParallel]);

  // ─── Create Payment Intent ───────────────────────────────────────────────

  const createPaymentIntent = useCallback(
    async (plan: Exclude<PlanId, 'free'>, billingPeriod: BillingPeriod, currency: PaymentCurrency = 'USDC') => {
      setError(null);
      try {
        const response = await apiFetch('/subscription/payment/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan, billingPeriod, currency }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          activePaymentIdRef.current = data.data.paymentId;
          const intent = data.data as PaymentIntent;
          return {
            ...intent,
            usdDisplay: `$${(intent.amountCents / 100).toFixed(2)}`,
          } as PaymentIntent;
        }
        const errMsg = data.error
          ? `${data.message} (${data.error})`
          : data.message || 'Failed to create payment intent';
        setError(errMsg);
        return null;
      } catch {
        setError('Network error while creating payment intent');
        return null;
      }
    },
    [],
  );

  // ─── Verify Payment ──────────────────────────────────────────────────────

  const verifyPayment = useCallback(
    async (
      paymentId: string,
      txHash: string,
    ): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> => {
      setError(null);
      try {
        const response = await apiFetch('/subscription/payment/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentId, txHash }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          // Refresh subscription and usage after verification
          await fetchSubscription();
          await fetchUsage();
          activePaymentIdRef.current = null;
          return { ok: true, data: data.data };
        }
        const errMsg = data.error
          ? `${data.message} (${data.error})`
          : data.message || 'Payment verification failed';
        setError(errMsg);
        return { ok: false, message: errMsg };
      } catch {
        const errMsg = 'Network error while verifying payment';
        setError(errMsg);
        return { ok: false, message: errMsg };
      }
    },
    [fetchSubscription, fetchUsage],
  );

  // ─── Attach tx hash to pending intent (lets backend sweep confirm it) ────

  const attachTxHash = useCallback(async (paymentId: string, txHash: string) => {
    try {
      const response = await apiFetch(`/subscription/payment/${paymentId}/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ txHash }),
      });
      const data = await response.json();
      if (response.ok && data.success) return true;
      return false;
    } catch {
      return false;
    }
  }, []);

  // ─── Polling for Pending Payments ────────────────────────────────────────

  const startPaymentPolling = useCallback(
    (
      paymentId: string,
      onConfirmed: () => void,
      timeoutMs: number = 120_000,
      txHash?: string,
    ) => {
      stopPaymentPolling();
      activePaymentIdRef.current = paymentId;

      const startTime = Date.now();
      let inFlight = false;
      // Permanent verification failures (wrong amount/token) — surface once.
      let lastPermanentError: string | null = null;

      const markConfirmed = async () => {
        stopPaymentPolling();
        await fetchSubscription();
        await fetchUsage();
        activePaymentIdRef.current = null;
        onConfirmed();
      };

      pollRef.current = setInterval(async () => {
        if (inFlight) return;
        if (Date.now() - startTime > timeoutMs) {
          stopPaymentPolling();
          setError(
            'Payment confirmation took too long. Your pre-paid subscription may still activate — check later.',
          );
          return;
        }

        inFlight = true;
        try {
          // Actively verify when we have a tx hash. Waiting only on the backend
          // sweep left the UI stuck when the sweep lagged or RPC failed once.
          if (txHash) {
            const verifyResponse = await apiFetch('/subscription/payment/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ paymentId, txHash }),
            });
            const verifyData = await verifyResponse.json();
            if (verifyResponse.ok && verifyData.success) {
              await markConfirmed();
              return;
            }
            const msg: string = verifyData.message || '';
            const retryable =
              /confirmations|not found on chain|details not found|try again/i.test(msg);
            if (!retryable && msg && msg !== lastPermanentError) {
              lastPermanentError = msg;
              setError(
                verifyData.error ? `${msg} (${verifyData.error})` : msg,
              );
            }
          }

          const response = await apiFetch(`/subscription/payment/${paymentId}`);
          const data = await response.json();
          if (response.ok && data.success && data.data.status === 'CONFIRMED') {
            await markConfirmed();
            return;
          }
          if (response.ok && data.success && data.data.status === 'FAILED') {
            stopPaymentPolling();
            setError(
              'Payment verification failed on the server. Use “verify manually” or contact support with your transaction hash.',
            );
          }
        } catch {
          // Silently fail polling — may be network blip
        } finally {
          inFlight = false;
        }
      }, 5000);
    },
    [stopPaymentPolling, fetchSubscription, fetchUsage],
  );

  // ─── Get Price for Plan ──────────────────────────────────────────────────

  const getPrice = useCallback(
    (plansParam: PlansResponse, planId: Exclude<PlanId, 'free'>, billingPeriod: BillingPeriod) => {
      return (
        plansParam.prices.find(
          (p) => p.planId === planId && p.billingPeriod === billingPeriod,
        ) ?? null
      );
    },
    [],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const getUsagePercentage = useCallback((window: UsageWindow): number => {
    if (window.limit === 0) return 0;
    return Math.min(100, (window.used / window.limit) * 100);
  }, []);

  const isQuotaExceeded = useCallback(
    (quota: QuotaStatus | null): boolean => {
      return quota !== null && !quota.canUse;
    },
    [],
  );

  const isApproachingLimit = useCallback(
    (usageParam: UsageResponse | null, threshold: number = 80): boolean => {
      if (!usageParam) return false;
      return (
        getUsagePercentage(usageParam.usage.daily) >= threshold ||
        getUsagePercentage(usageParam.usage.weekly) >= threshold ||
        getUsagePercentage(usageParam.usage.monthly) >= threshold
      );
    },
    [getUsagePercentage],
  );

  // ─── Cancel Subscription ─────────────────────────────────────────────────
  // Sets cancelAtPeriodEnd = true. Subscription stays active until expiresAt.

  const cancelSubscription = useCallback(async () => {
    setError(null);
    try {
      const response = await apiFetch('/subscription/cancel', {
        method: 'POST',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSubscription((prev) =>
          prev ? { ...prev, cancelAtPeriodEnd: true } : null,
        );
        return true;
      }
      setError(data.message || 'Failed to cancel pre-paid subscription');
      return false;
    } catch {
      setError('Network error while cancelling pre-paid subscription');
      return false;
    }
  }, []);

  // ─── Refresh All ─────────────────────────────────────────────────────────

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchSubscription(), fetchUsage()]);
  }, [fetchSubscription, fetchUsage]);

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      // State
      plans,
      subscription,
      usage,
      quotaStatus,
      paymentHistory,
      isLoadingPlans,
      isLoadingSubscription,
      isLoadingUsage,
      isLoadingPaymentHistory,
      error,

      // Actions
      fetchPlans,
      fetchSubscription,
      fetchUsage,
      fetchPaymentHistory,
      createPaymentIntent,
      verifyPayment,
      attachTxHash,
      startPaymentPolling,
      stopPaymentPolling,
      refreshAll,
      cancelSubscription,
      getPrice,

      // Derived
      getUsagePercentage,
      isQuotaExceeded,
      isApproachingLimit,
      isPendingPayment: activePaymentIdRef.current !== null,
    }),
    [
      plans,
      subscription,
      usage,
      quotaStatus,
      paymentHistory,
      isLoadingPlans,
      isLoadingSubscription,
      isLoadingUsage,
      isLoadingPaymentHistory,
      error,
      fetchPlans,
      fetchSubscription,
      fetchUsage,
      fetchPaymentHistory,
      createPaymentIntent,
      verifyPayment,
      attachTxHash,
      startPaymentPolling,
      stopPaymentPolling,
      refreshAll,
      cancelSubscription,
      getPrice,
      getUsagePercentage,
      isQuotaExceeded,
      isApproachingLimit,
    ],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// ─── Consumer hook ────────────────────────────────────────────────────────

export function useSubscriptionContext(): SubscriptionContextValue {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}


